/**
 * BulkActionsBar — barra de acciones para multi-select en las tablas
 * de aseos (round 2026-05-12 iter 39).
 *
 * Aparece encima de cada tabla cuando hay filas seleccionadas. Ofrece:
 *  - Cambiar estado (Dropdown con 4 opciones del ENUM).
 *  - Cambiar urgencia (Dropdown con 4 opciones).
 *  - Eliminar (Popconfirm con conteo).
 *  - Limpiar selección.
 *
 * El padre maneja el state de seleccionados y los handlers de mutación;
 * este componente es puramente presentacional.
 */

import React from "react";
import { Button, Dropdown, Space, Tag, Popconfirm } from "antd";
import {
  DeleteOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  DownOutlined,
  CloseOutlined,
} from "@ant-design/icons";

type Estado = "pendiente" | "en_proceso" | "completado" | "pagado";
type Urgencia = "am" | "pm" | "critico 11:00" | "11:00";

interface Props {
  selectedIds: number[];
  onClear: () => void;
  onChangeEstado: (estado: Estado) => void;
  onChangeUrgencia: (urgencia: Urgencia) => void;
  onDelete: () => void;
  disabled?: boolean;
}

const ESTADO_OPTIONS: { key: Estado; label: string }[] = [
  { key: "pendiente", label: "Pendiente" },
  { key: "en_proceso", label: "En proceso" },
  { key: "completado", label: "Completado" },
  { key: "pagado", label: "Pagado" },
];

const URGENCIA_OPTIONS: { key: Urgencia; label: string }[] = [
  { key: "am", label: "AM" },
  { key: "pm", label: "PM" },
  { key: "critico 11:00", label: "Crítico 11:00" },
  { key: "11:00", label: "11:00" },
];

const BulkActionsBar: React.FC<Props> = ({
  selectedIds,
  onClear,
  onChangeEstado,
  onChangeUrgencia,
  onDelete,
  disabled = false,
}) => {
  if (selectedIds.length === 0) return null;

  const estadoMenu = {
    items: ESTADO_OPTIONS.map((o) => ({
      key: o.key,
      label: o.label,
      onClick: () => onChangeEstado(o.key),
    })),
  };

  const urgenciaMenu = {
    items: URGENCIA_OPTIONS.map((o) => ({
      key: o.key,
      label: o.label,
      onClick: () => onChangeUrgencia(o.key),
    })),
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        marginBottom: 12,
        background: "#e6f4ff",
        border: "1px solid #91caff",
        borderRadius: 8,
      }}
    >
      <Tag color="blue" style={{ fontSize: 13, padding: "2px 8px" }}>
        {selectedIds.length} aseo{selectedIds.length === 1 ? "" : "s"}{" "}
        seleccionado{selectedIds.length === 1 ? "" : "s"}
      </Tag>

      <Space size={8}>
        <Dropdown menu={estadoMenu} disabled={disabled}>
          <Button icon={<CheckOutlined />}>
            Cambiar estado <DownOutlined />
          </Button>
        </Dropdown>

        <Dropdown menu={urgenciaMenu} disabled={disabled}>
          <Button icon={<ClockCircleOutlined />}>
            Cambiar urgencia <DownOutlined />
          </Button>
        </Dropdown>

        <Popconfirm
          title={`¿Eliminar ${selectedIds.length} aseo${selectedIds.length === 1 ? "" : "s"}?`}
          description="Esta acción no se puede deshacer."
          okText="Sí, eliminar"
          okButtonProps={{ danger: true }}
          cancelText="Cancelar"
          onConfirm={onDelete}
        >
          <Button danger icon={<DeleteOutlined />} disabled={disabled}>
            Eliminar
          </Button>
        </Popconfirm>
      </Space>

      <Button
        type="text"
        icon={<CloseOutlined />}
        onClick={onClear}
        style={{ marginLeft: "auto" }}
      >
        Limpiar selección
      </Button>
    </div>
  );
};

export default BulkActionsBar;
